/**
 * MagnetStructured — the EE-owned structured render for lead-magnet landing
 * and thank-you pages (Phase 2 of the structured-content plan; contract in
 * CC's docs/LEAD-MAGNET-STRUCTURED-CONTENT-HANDOFF.md, approved 2026-09-08).
 *
 * This is the one-time port of the "Broadcast" look CC's legacy full-doc HTML
 * shipped — the gold duotone hero, numbered index rows, navy proof band,
 * testimonial cards — into EE-owned components. From here the look is EE's
 * property: CC ships pure copy (LandingContentV1 / ThankYouContentV1) and no
 * CC CSS, font, or class name ever crosses the feed again.
 *
 * Presentation rules (per the contract + EE conventions):
 *  - ONE font stack: Inter (already loaded by taker/index.html), with a
 *    SYSTEM monospace stack for the small technical accents (eyebrow labels,
 *    index numbers, quote attributions, the attribution line) — no second
 *    webfont request, ever. Poppins/JetBrains Mono died with the legacy HTML.
 *  - Sub-brand aware like MagnetHeader/Footer: the CTA/accent color comes
 *    from the resolved brand payload (paletteAccent); the structural tokens
 *    below (gold duotone, midnight navy, inks) are EE constants.
 *  - Scoped CSS only: every rule lives under an `.eelm-` class. No `*` reset,
 *    no html/body rules — the acceptance test is literally "zero CC CSS in
 *    EE's DOM," and this component must not become the new global-leak.
 *  - Rich fields are sanitized to the <strong>/<em>/<br> allowlist at render
 *    (sanitizeRich), regardless of what arrives. Plain fields render as React
 *    text nodes. `attribution` always renders — it carries the IP posture.
 *
 * The capture form is passed in as `children` (MagnetPage owns CaptureForm) —
 * rendered inside the hero form card, replacing the legacy {{OPT_IN_FORM}}
 * substitution. The `.eelm-form-card` rules below style the form's bare
 * inputs/button, exactly the role CC's `.lh-form-card` CSS used to play.
 */

import { useMemo, type ReactNode } from 'react';
import MagnetHeader, { paletteAccent, type MagnetHeaderBrand } from './MagnetHeader';
import MagnetFooter from './MagnetFooter';
import {
  sanitizeRich,
  type MagnetLandingContent,
  type MagnetThankyouContent,
} from '../lib/magnetContent';

// --- EE-owned structural tokens (the Broadcast look) ------------------------

const NAVY = '#0A0A2E';
const INK = '#0A0A2E';
const INK_SOFT = '#4A4A6A';
const BG = '#FAFAFF';
const BG_SOFT = '#F5F5FF';
const RULE = 'rgba(10,10,46,0.10)';
const DUO_GOLD_SH = '#2A1D00';
const DUO_GOLD_HL = '#FFD320';
const DUO_GOLD_MID = '#806000';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, monospace";

/** The form-card anchor the header CTA and in-page CTAs scroll to. */
export const STRUCTURED_FORM_ID = 'eelm-get';

// Scoped stylesheet — every selector under .eelm-root; media queries are why
// this is a <style> block rather than inline styles. The accent rides the
// --eelm-accent custom property set on the root from the brand palette.
/** Short label for every structured magnet's submit and header buttons. hero.cta_line is a full sentence
 *  and stays as the heading above the form. */
export const MAGNET_SUBMIT_LABEL = 'Send it to me';

const CSS = `
  .eelm-root { background:${BG}; color:${INK}; font-family:${FONT}; font-weight:400; line-height:1.55; font-size:16px; -webkit-font-smoothing:antialiased; }
  .eelm-root h1, .eelm-root h2, .eelm-root h3, .eelm-root p, .eelm-root ul, .eelm-root ol { margin:0; padding:0; }
  .eelm-page { max-width:880px; width:100%; margin:0 auto; box-sizing:border-box; padding:clamp(40px,6vw,64px) clamp(20px,5vw,48px) clamp(56px,8vw,88px); }

  .eelm-hero { position:relative; border-radius:22px; overflow:hidden; padding:clamp(32px,5vw,56px); color:#fff;
    background:linear-gradient(140deg, ${DUO_GOLD_SH} 0%, ${DUO_GOLD_MID} 100%);
    box-shadow:0 24px 48px -16px rgba(10,10,46,0.30), 0 8px 20px -10px rgba(10,10,46,0.20); }
  .eelm-hero::after { content:''; position:absolute; inset:0; pointer-events:none; opacity:0.34;
    background:radial-gradient(70% 80% at 112% 116%, ${DUO_GOLD_HL} 0%, transparent 55%); }
  .eelm-hero > * { position:relative; z-index:1; }
  .eelm-hero-grid { display:grid; grid-template-columns:1fr; gap:clamp(24px,4vw,40px); align-items:center; }
  @media (min-width:820px) { .eelm-hero-grid { grid-template-columns:1fr 300px; } }

  .eelm-hero-tag { display:inline-flex; align-items:center; gap:8px; background:${DUO_GOLD_HL}; color:${DUO_GOLD_SH}; padding:8px 14px; border-radius:999px; font-size:12px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; margin-bottom:18px; }
  .eelm-hero h1 { font-size:clamp(30px,5vw,52px); font-weight:800; letter-spacing:-0.035em; line-height:1.06; margin-bottom:14px; color:#fff; max-width:18ch; }
  .eelm-hero-sub { font-size:clamp(16px,1.4vw,19px); line-height:1.55; color:rgba(255,255,255,0.9); max-width:50ch; }
  .eelm-cover-wrap { display:flex; justify-content:center; }
  .eelm-cover { display:block; width:100%; max-width:280px; height:auto; border-radius:12px; box-shadow:0 18px 40px -12px rgba(0,0,0,0.5); }

  .eelm-form-card { margin-top:clamp(24px,4vw,36px); background:#fff; border-radius:18px; padding:clamp(22px,3vw,32px); box-shadow:0 16px 36px -16px rgba(10,10,46,0.35); scroll-margin-top:88px; }
  .eelm-cta-line { font-size:clamp(18px,2vw,22px); font-weight:800; color:${INK}; letter-spacing:-0.02em; margin-bottom:14px; }
  .eelm-form-bullets { list-style:none; margin:0 0 18px; display:grid; gap:8px; }
  .eelm-form-bullets li { position:relative; padding-left:26px; font-size:14.5px; color:${INK_SOFT}; }
  .eelm-form-bullets li::before { content:'\\2713'; position:absolute; left:0; top:0; color:var(--eelm-accent); font-weight:800; }
  .eelm-form-card input[type="email"], .eelm-form-card input[type="text"] { width:100%; box-sizing:border-box; padding:14px 18px; border:1px solid rgba(10,10,46,0.18); border-radius:999px; font-family:inherit; font-size:15px; color:${INK}; background:${BG_SOFT}; margin-bottom:12px; }
  .eelm-form-card input:focus { outline:none; border-color:var(--eelm-accent); box-shadow:0 0 0 3px color-mix(in srgb, var(--eelm-accent) 18%, transparent); }
  .eelm-form-card button { display:inline-flex; align-items:center; justify-content:center; gap:8px; width:100%; padding:15px 28px; border:none; border-radius:999px; background:var(--eelm-accent); color:#fff; font-family:inherit; font-size:16px; font-weight:600; letter-spacing:0.01em; cursor:pointer; transition:transform 150ms ease, box-shadow 150ms ease; }
  .eelm-form-card button:hover { transform:translateY(-2px); box-shadow:0 12px 28px -8px color-mix(in srgb, var(--eelm-accent) 55%, transparent); }
  .eelm-form-card input:focus-visible, .eelm-form-card button:focus-visible, .eelm-cta2:focus-visible { outline:2px solid var(--eelm-accent); outline-offset:2px; }
  /* Prefixed with .eelm-root: the reset above (.eelm-root p { margin:0 }) outranks a bare class, which had zeroed this margin. */
  .eelm-root .eelm-form-note { font-size:12px; color:${INK_SOFT}; margin-top:18px; font-family:${MONO}; letter-spacing:0.02em; }
  .eelm-why-free { font-size:13px; color:${INK_SOFT}; margin-top:8px; }

  .eelm-proof { margin-top:clamp(28px,4vw,40px); background:${NAVY}; color:#fff; border-radius:22px; padding:clamp(24px,3.5vw,34px); display:grid; gap:18px; }
  .eelm-proof-lead { font-size:clamp(15px,1.4vw,18px); line-height:1.5; color:rgba(255,255,255,0.92); }
  .eelm-proof-lead strong { color:${DUO_GOLD_HL}; font-weight:700; }
  .eelm-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
  @media (max-width:560px){ .eelm-stats { grid-template-columns:1fr 1fr; } }
  .eelm-stat { background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.12); border-radius:14px; padding:16px 18px; }
  .eelm-stat .n { font-size:clamp(22px,3vw,30px); font-weight:800; color:#fff; letter-spacing:-0.02em; line-height:1; }
  .eelm-stat .l { font-size:12.5px; color:rgba(255,255,255,0.7); margin-top:6px; }

  .eelm-section { margin-top:clamp(48px,7vw,80px); }
  .eelm-eyebrow { font-size:12px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:${INK_SOFT}; font-family:${MONO}; margin-bottom:10px; }
  .eelm-h2 { font-size:clamp(24px,3.2vw,36px); font-weight:800; letter-spacing:-0.03em; line-height:1.08; color:${INK}; }

  .eelm-index { list-style:none; margin:24px 0 0; }
  .eelm-idx { display:grid; grid-template-columns:48px 4px 1fr; gap:18px; align-items:start; padding:clamp(18px,2.4vw,26px) 0; border-top:1px solid ${RULE}; }
  .eelm-idx:last-child { border-bottom:1px solid ${RULE}; }
  .eelm-idx-num { font-family:${MONO}; font-size:14px; font-weight:500; color:${INK_SOFT}; padding-top:2px; }
  .eelm-idx-stripe { align-self:stretch; min-height:36px; border-radius:2px; background:var(--eelm-accent); }
  .eelm-idx-title { font-size:clamp(17px,1.6vw,20px); font-weight:700; letter-spacing:-0.015em; line-height:1.3; color:${INK}; }
  @media (max-width:560px) { .eelm-idx { grid-template-columns:34px 3px 1fr; gap:12px; } }

  .eelm-quotes { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin-top:24px; }
  @media (max-width:680px){ .eelm-quotes { grid-template-columns:1fr; } }
  .eelm-quote { background:#fff; border:1px solid ${RULE}; border-radius:18px; padding:22px 22px 18px; box-shadow:0 10px 24px -16px rgba(10,10,46,0.25); }
  .eelm-quote .qm { color:var(--eelm-accent); font-size:34px; line-height:0.6; font-weight:800; }
  .eelm-quote p { font-size:15.5px; color:${INK}; margin:6px 0 12px; font-weight:500; }
  .eelm-quote .who { font-size:12.5px; color:${INK_SOFT}; font-family:${MONO}; letter-spacing:0.02em; }
  .eelm-quotes-foot { margin-top:16px; font-size:15px; color:${INK_SOFT}; }
  .eelm-quotes-foot strong { color:${INK}; font-weight:700; }

  .eelm-who { margin-top:20px; background:${BG_SOFT}; border-radius:22px; padding:clamp(24px,3.5vw,36px); }
  .eelm-who p { font-size:clamp(15px,1.3vw,17px); line-height:1.6; color:${INK_SOFT}; margin:0 0 12px; }
  .eelm-who p:last-child { margin:0; }

  .eelm-cta2-wrap { margin-top:28px; text-align:center; }
  .eelm-cta2 { display:inline-flex; align-items:center; gap:8px; background:var(--eelm-accent); color:#fff; text-decoration:none; font-weight:700; font-size:16px; padding:15px 30px; border-radius:999px; transition:transform 150ms ease, box-shadow 150ms ease; box-shadow:0 12px 28px -10px color-mix(in srgb, var(--eelm-accent) 55%, transparent); }
  .eelm-cta2:hover { transform:translateY(-2px); }
  .eelm-cta2-note { display:block; margin-top:8px; font-size:12.5px; color:${INK_SOFT}; }

  .eelm-attrib { margin-top:clamp(40px,6vw,64px); padding-top:20px; border-top:1px solid ${RULE}; font-size:13px; color:${INK_SOFT}; font-family:${MONO}; letter-spacing:0.01em; line-height:1.6; }

  /* Thank-you */
  .eelm-ty-card { max-width:780px; margin:32px auto; background:#fff; border:1px solid ${RULE}; border-radius:14px; padding:clamp(28px,5vw,48px) clamp(24px,6vw,56px); box-sizing:border-box; }
  .eelm-ty-card h1 { color:${NAVY}; font-size:clamp(24px,3.4vw,30px); line-height:1.16; margin:0 0 8px; font-weight:700; letter-spacing:-0.02em; }
  .eelm-ty-body p { font-size:16px; margin:0 0 14px; color:${INK_SOFT}; }
  .eelm-ty-next { margin:26px 0 0; padding:26px 28px; background:${NAVY}; color:#eaf0fb; border-radius:12px; }
  .eelm-ty-next .k { color:var(--eelm-accent); font-weight:600; font-size:12px; letter-spacing:.08em; text-transform:uppercase; margin:0 0 8px; font-family:${MONO}; }
  .eelm-ty-next p { color:rgba(255,255,255,0.82); font-size:15px; margin:0 0 12px; }
  .eelm-ty-next p strong { color:#fff; font-size:19px; line-height:1.3; display:inline-block; }
  .eelm-ty-cta { display:inline-block; background:var(--eelm-accent); color:#fff; text-decoration:none; font-weight:600; font-size:16px; padding:14px 28px; border-radius:8px; margin-top:4px; }
  .eelm-ty-alts { margin:22px 0 0; list-style:none; }
  .eelm-ty-alts li { padding:10px 0; border-top:1px solid ${RULE}; font-size:15px; color:${INK_SOFT}; }
  .eelm-ty-alts li:first-child { border-top:none; }
  .eelm-ty-alts a { color:var(--eelm-accent); font-weight:600; text-decoration:none; }

  @media (prefers-reduced-motion:reduce) {
    .eelm-root *, .eelm-root *::before, .eelm-root *::after { transition-duration:0.01ms !important; }
  }
`;

function rich(html: string) {
  return { __html: sanitizeRich(html) };
}

function pad2(n: number): string {
  return String(n + 1).padStart(2, '0');
}

export interface MagnetFunnelUrls {
  funnel: string | null;
  cbl: string | null;
  call: string | null;
}

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------

export function MagnetStructuredLanding({
  content,
  brand,
  coverUrl,
  coverAlt,
  children,
}: {
  content: MagnetLandingContent;
  brand: MagnetHeaderBrand | null;
  coverUrl: string | null;
  coverAlt: string;
  /** The live capture form (MagnetPage's CaptureForm, variant "bare") —
   *  rendered inside the hero form card where {{OPT_IN_FORM}} used to sit. */
  children: ReactNode;
}) {
  const accent = useMemo(() => paletteAccent(brand?.palette), [brand]);
  const scrollToForm = () => {
    document.getElementById(STRUCTURED_FORM_ID)?.scrollIntoView({ block: 'start' });
  };
  const { hero, proof, whats_inside, testimonials, who_its_for, secondary_cta } = content;

  return (
    <>
      <MagnetHeader brand={brand} ctaLabel={MAGNET_SUBMIT_LABEL} onCta={scrollToForm} />
      <div className="eelm-root" style={{ ['--eelm-accent' as string]: accent }}>
        <style>{CSS}</style>
        <main className="eelm-page">
          <section className="eelm-hero">
            <div className="eelm-hero-grid">
              <div>
                {hero.tag && <span className="eelm-hero-tag">{hero.tag}</span>}
                <h1>{hero.hook}</h1>
                <p className="eelm-hero-sub">{hero.subhook}</p>
              </div>
              {coverUrl && (
                <div className="eelm-cover-wrap">
                  <img
                    className="eelm-cover"
                    src={coverUrl}
                    alt={`${coverAlt} — cover`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
            <div className="eelm-form-card" id={STRUCTURED_FORM_ID}>
              <p className="eelm-cta-line">{hero.cta_line}</p>
              {hero.bullets && hero.bullets.length > 0 && (
                <ul className="eelm-form-bullets">
                  {hero.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
              {children}
              <p className="eelm-form-note">{hero.form_note}</p>
              {hero.reassurance && <p className="eelm-why-free">{hero.reassurance}</p>}
            </div>
          </section>

          {proof && (
            <section className="eelm-proof">
              <p className="eelm-proof-lead" dangerouslySetInnerHTML={rich(proof.intro)} />
              <div className="eelm-stats">
                {proof.stats.map((s, i) => (
                  <div className="eelm-stat" key={i}>
                    <div className="n">{s.value}</div>
                    <div className="l">{s.label}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="eelm-section">
            <p className="eelm-eyebrow">{whats_inside.eyebrow}</p>
            <h2 className="eelm-h2">{whats_inside.heading}</h2>
            <ol className="eelm-index">
              {whats_inside.items.map((item, i) => (
                <li className="eelm-idx" key={i}>
                  <span className="eelm-idx-num">{pad2(i)}</span>
                  <span className="eelm-idx-stripe" aria-hidden="true" />
                  <span className="eelm-idx-title" dangerouslySetInnerHTML={rich(item)} />
                </li>
              ))}
            </ol>
            {/* EE presentation: repeat the hero CTA mid-page (anchor to the form),
                the same rhythm the legacy page carried after this section. */}
            <div className="eelm-cta2-wrap">
              <a className="eelm-cta2" href={`#${STRUCTURED_FORM_ID}`}>{MAGNET_SUBMIT_LABEL} →</a>
            </div>
          </section>

          {testimonials && (
            <section className="eelm-section">
              <p className="eelm-eyebrow">{testimonials.eyebrow}</p>
              <h2 className="eelm-h2">{testimonials.heading}</h2>
              <div className="eelm-quotes">
                {testimonials.quotes.map((q, i) => (
                  <div className="eelm-quote" key={i}>
                    <div className="qm">&ldquo;</div>
                    <p dangerouslySetInnerHTML={rich(q.text)} />
                    <div className="who">{q.attribution}</div>
                  </div>
                ))}
              </div>
              {testimonials.outro && (
                <p className="eelm-quotes-foot" dangerouslySetInnerHTML={rich(testimonials.outro)} />
              )}
            </section>
          )}

          <section className="eelm-section">
            <p className="eelm-eyebrow">{who_its_for.eyebrow}</p>
            <h2 className="eelm-h2">{who_its_for.heading}</h2>
            <div className="eelm-who">
              {who_its_for.paragraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={rich(p)} />
              ))}
            </div>
            {secondary_cta && (
              <div className="eelm-cta2-wrap">
                <a className="eelm-cta2" href={`#${STRUCTURED_FORM_ID}`}>{secondary_cta.label} →</a>
                {secondary_cta.note && <span className="eelm-cta2-note">{secondary_cta.note}</span>}
              </div>
            )}
          </section>

          {/* REQUIRED — the IP posture line. Never conditionally dropped: the
              parser refuses content without it, so it is always present here. */}
          <p className="eelm-attrib">{content.attribution}</p>
        </main>
      </div>
      <MagnetFooter brand={brand} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Thank-you
// ---------------------------------------------------------------------------

export function MagnetStructuredThankyou({
  content,
  brand,
  funnelUrls,
}: {
  content: MagnetThankyouContent;
  brand: MagnetHeaderBrand | null;
  /** Resolved by the worker (funnel_target_url + EE's standing CBL/call magnet
   *  tokens). `funnel` null → the next-step card renders with no button, per
   *  the v1 contract. cbl/call feed the alternates strip below the card — EE
   *  presentation standing in for the legacy thank-you's two alternate links
   *  (not part of schema v1; revisit if CC wants copy control in v2). */
  funnelUrls: MagnetFunnelUrls;
}) {
  const accent = useMemo(() => paletteAccent(brand?.palette), [brand]);
  const { headline, confirmation, next_step } = content;

  return (
    <>
      <MagnetHeader brand={brand} />
      <div className="eelm-root" style={{ ['--eelm-accent' as string]: accent, minHeight: '60vh', padding: '0 20px' }}>
        <style>{CSS}</style>
        <div className="eelm-ty-card">
          <h1>{headline}</h1>
          <div className="eelm-ty-body">
            {confirmation.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={rich(p)} />
            ))}
          </div>

          <div className="eelm-ty-next">
            <p className="k">{next_step.heading}</p>
            {next_step.paragraphs.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={rich(p)} />
            ))}
            {funnelUrls.funnel && (
              <a className="eelm-ty-cta" href={funnelUrls.funnel}>{next_step.cta_label} →</a>
            )}
          </div>

          {(funnelUrls.cbl || funnelUrls.call) && (
            <ul className="eelm-ty-alts">
              {funnelUrls.cbl && (
                <li>
                  Prefer to see the whole system mapped out live?{' '}
                  <a href={funnelUrls.cbl}>Join Client Blueprint Live with Lisa →</a>
                </li>
              )}
              {funnelUrls.call && (
                <li>
                  Rather just talk it through?{' '}
                  <a href={funnelUrls.call}>Book a call with our team →</a>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
      <MagnetFooter brand={brand} />
    </>
  );
}
